import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

export const PAGE_SIZES = [25, 50, 100];

// Build a compact list of page tokens (numbers + "…") for the page buttons.
function buildPages(page, pageCount) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const pages = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(pageCount - 1, page + 1);
  if (left > 2) pages.push("…l");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < pageCount - 1) pages.push("…r");
  pages.push(pageCount);
  return pages;
}

/**
 * Reusable pagination control: results summary + per-page selector + page buttons.
 * Pair with the usePagination hook.
 */
export const TablePagination = ({
  page, pageCount, pageSize, total, start, end,
  onPageChange, onPageSizeChange, sizes = PAGE_SIZES, testid = "pagination",
}) => {
  const { t } = useT();
  if (!total) return null;
  const pages = buildPages(page, pageCount);

  return (
    <div
      className="mt-3 flex flex-col items-center justify-between gap-3 border-t border-border pt-3 sm:flex-row"
      data-testid={testid}
    >
      <div className="text-sm text-muted-foreground" data-testid={`${testid}-summary`}>
        {t("pager.showing", { from: start + 1, to: end, total })}
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{t("pager.perPage")}</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="h-8 w-[74px]" data-testid={`${testid}-size`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sizes.map((s) => (
                <SelectItem key={s} value={String(s)} data-testid={`${testid}-size-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline" size="icon" className="h-8 w-8"
            disabled={page <= 1} onClick={() => onPageChange(page - 1)}
            aria-label={t("pager.prev")} data-testid={`${testid}-prev`}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {pages.map((p) =>
            typeof p === "string" ? (
              <span key={p} className="px-1 text-sm text-muted-foreground">…</span>
            ) : (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                size="icon" className="h-8 w-8"
                onClick={() => onPageChange(p)}
                aria-current={p === page ? "page" : undefined}
                data-testid={`${testid}-page-${p}`}
              >
                {p}
              </Button>
            ),
          )}

          <Button
            variant="outline" size="icon" className="h-8 w-8"
            disabled={page >= pageCount} onClick={() => onPageChange(page + 1)}
            aria-label={t("pager.next")} data-testid={`${testid}-next`}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
